'use client';

import { useEffect, useRef, useState } from 'react';
import { Send, Loader2, X, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
}

interface ChatWindowProps {
  /** Si se cierra (widget). Si omitido, layout permanente (/ayuda). */
  onClose?: () => void;
  /** Source context al crear session. */
  source?: 'widget' | 'ayuda_page';
  /** Mensaje inicial del assistant al abrir. */
  greeting?: string;
}

const DEFAULT_GREETING =
  'Hola, soy el asistente NQORO. Puedo ayudarte con el catálogo, estado de pedidos o escalar tu caso a un humano. ¿En qué te ayudo?';

export function ChatWindow({
  onClose,
  source = 'widget',
  greeting = DEFAULT_GREETING,
}: ChatWindowProps) {
  const [messages, setMessages] = useState<Message[]>([
    { id: 'greeting', role: 'assistant', content: greeting },
  ]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function send() {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');

    const userMsg: Message = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: text,
    };
    const assistantId = `a-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      userMsg,
      { id: assistantId, role: 'assistant', content: '', isStreaming: true },
    ]);
    setStreaming(true);

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          message: text,
          ...(sessionId ? { sessionId } : {}),
          source,
        }),
      });
      if (!res.ok) {
        const errBody = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(
          errBody?.error === 'rate_limit'
            ? 'Demasiados mensajes, espera un minuto'
            : (errBody?.error ?? 'error')
        );
      }
      if (!res.body) throw new Error('sin stream');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let acc = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() ?? '';
        for (const block of events) {
          const lines = block.split('\n');
          const eventLine = lines.find((l) => l.startsWith('event: '));
          const dataLine = lines.find((l) => l.startsWith('data: '));
          if (!eventLine || !dataLine) continue;
          const evtType = eventLine.slice(7).trim();
          const data = JSON.parse(dataLine.slice(6)) as Record<string, unknown>;
          if (evtType === 'session' && typeof data['sessionId'] === 'string') {
            setSessionId(data['sessionId']);
          } else if (evtType === 'text' && typeof data['delta'] === 'string') {
            acc += data['delta'];
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? { ...m, content: acc } : m))
            );
          } else if (evtType === 'tool_start') {
            // Indicador sutil de tool usage
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? {
                      ...m,
                      content: acc || `Consultando ${String(data['name'])}…`,
                    }
                  : m
              )
            );
          } else if (evtType === 'error') {
            throw new Error(String(data['error'] ?? 'error'));
          }
        }
      }

      setMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m))
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Algo salió mal';
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId ? { ...m, content: `⚠️ ${msg}`, isStreaming: false } : m
        )
      );
    } finally {
      setStreaming(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal={onClose ? 'true' : undefined}
      aria-label="Chat con el asistente NQORO"
      className="flex h-full flex-col overflow-hidden bg-[var(--color-bg)]"
    >
      <header className="flex items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[var(--color-accent)]" strokeWidth={1.25} />
          <div>
            <p className="type-body-sm text-[var(--color-fg)]">Asistente NQORO</p>
            <p className="type-caption text-[var(--color-fg-subtle)]">
              {streaming ? 'escribiendo…' : 'Estoy aquí'}
            </p>
          </div>
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar chat"
            className="rounded-xs p-1.5 text-[var(--color-fg-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-accent)]"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </header>

      <div className="flex-1 space-y-4 overflow-y-auto px-4 py-5">
        {messages.map((m) => (
          <div
            key={m.id}
            className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}
          >
            <div
              className={cn(
                'type-body-sm max-w-[85%] rounded-sm px-3 py-2 whitespace-pre-wrap',
                m.role === 'user'
                  ? 'bg-[var(--color-accent)] text-[var(--color-accent-fg)]'
                  : 'border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-fg)]'
              )}
            >
              {m.content}
              {m.isStreaming && !m.content ? (
                <Loader2 className="h-3 w-3 animate-spin text-[var(--color-fg-muted)]" />
              ) : null}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <footer className="border-t border-[var(--color-border)] bg-[var(--color-surface)] p-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
          className="flex items-end gap-2"
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            placeholder="Escribe tu mensaje…"
            rows={1}
            disabled={streaming}
            maxLength={4000}
            className="type-body-sm flex-1 resize-none rounded-xs border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-[var(--color-fg)] placeholder:text-[var(--color-fg-subtle)] focus:border-[var(--color-accent)] focus:outline-none"
          />
          <button
            type="submit"
            disabled={streaming || input.trim().length === 0}
            aria-label="Enviar"
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xs',
              streaming || input.trim().length === 0
                ? 'cursor-not-allowed border border-[var(--color-border)] text-[var(--color-fg-subtle)]'
                : 'bg-[var(--color-accent)] text-[var(--color-accent-fg)] hover:bg-[var(--color-accent)]'
            )}
          >
            {streaming ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </form>
        <p className="type-caption mt-2 text-center text-[var(--color-fg-subtle)]">
          IA conversacional · puede tener errores. Para casos críticos, soporte@nqoro.com.
        </p>
      </footer>
    </div>
  );
}
